import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import React, {useMemo, useState} from 'react'
import {generatePath} from 'react-router'
import {Routes} from '../../common/Routes'
import {usePersistedState} from '../../common/hooks/usePersistedState'
import {Box} from '../../webui/Box'
import {Button} from '../../webui/Button'
import {LoadingSpinnerContainer} from '../../webui/progress/LoadingSpinnerContainer'
import {SortablePaginatedTable} from '../../webui/table/sortable-paginated-table/SortablePaginatedTable'
import {Typography} from '../../webui/Typography'
import {ActionPageFilter, CompartmentThatNeedsActionFilterState} from './ActionPageFilter'
import {ActionsMap} from './ActionsMap'
import {createCompartmentsThatNeedsActionData} from './utils/createCompartmentsThatNeedsActionData'
import {filterCompartmentsThatNeedsActionData} from './utils/filterCompartmentsThatNeedsActionData'
import {resolveActionMarkers} from './utils/resolveActionMarkers'
import {contractTypeLabel} from '../../common/domain/contractType'
import {useAllBoxesForActions} from './gql/useAllBoxesForActions'
import {useAllOrganisations} from '../organisation/gql/useAllOrganisations'
import {
  compartmentsThatNeedsActionComparator,
  createCompartmentsThatNeedsActionTableHeadCells,
  INITIAL_ROWS_PER_PAGE,
} from './CompartmentsThatNeedActionTable'
import {Paper} from '@mui/material'
import {Stack} from '../../webui/Stack'

interface Props {}

const ACTIONS_MAP_CENTER: [number, number] = [59.5, 15]
const ACTIONS_MAP_ZOOM = 6
const ACTIONS_MAP_HEIGHT = 'min(900px, calc(100vh - 160px))'

const initialFilterState: CompartmentThatNeedsActionFilterState = {
  boxName: {value: ''},
  endDate: {value: ''},
  fromDate: {value: ''},
  locked: {value: null},
  organisations: {value: [], include: true},
  products: {value: [], include: true},
  productStatuses: {value: [], include: true},
  contractTypes: {value: [], include: true},
}

const LEGEND_ITEMS = [
  {label: 'High', color: 'red'},
  {label: 'Medium', color: '#ff7400'},
  {label: 'Low', color: '#fcba03'},
] as const

const SectionHeader: React.FC<{
  compartmentCount: number
  showMap: boolean
  onToggleMap: () => void
}> = ({compartmentCount, showMap, onToggleMap}) => (
  <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" alignItems={{xs: 'flex-start', sm: 'center'}} spacing={1} mb={2}>
    <Box>
      <Typography variant="h3">Compartments that need action</Typography>
      <Typography variant="body2" color="textSecondary" sx={{mt: 0.5}}>
        {compartmentCount} compartment{compartmentCount === 1 ? '' : 's'} requiring action
      </Typography>
    </Box>
    <Button size="small" startIcon={<MapOutlinedIcon />} onClick={onToggleMap}>
      {showMap ? 'Hide map' : 'Show map'}
    </Button>
  </Stack>
)

const MapLegend: React.FC = () => (
  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
    {LEGEND_ITEMS.map(({label, color}) => (
      <Stack key={label} direction="row" alignItems="center" spacing={0.75}>
        <Box sx={{width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0}} />
        <Typography variant="caption" color="textSecondary">
          {label}
        </Typography>
      </Stack>
    ))}
  </Stack>
)

const MapPanel: React.FC<{
  actionMarkers: ReturnType<typeof resolveActionMarkers>
}> = ({actionMarkers}) => (
  <Box
    sx={{
      position: {lg: 'sticky'},
      top: {lg: 16},
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
    }}
  >
    <Box display="flex" justifyContent="space-between" alignItems="center" p={2} minHeight={56}>
      <Typography variant="subtitle1" fontWeight={600}>
        Locations ({actionMarkers.length})
      </Typography>
    </Box>

    <Paper variant="outlined" sx={{overflow: 'hidden', width: '100%', flex: 1}}>
      <ActionsMap actions={actionMarkers} height={ACTIONS_MAP_HEIGHT} minHeight={480} center={ACTIONS_MAP_CENTER} zoom={ACTIONS_MAP_ZOOM} />
      <Box px={2} py={1.5} borderTop={1} borderColor="divider">
        <MapLegend />
      </Box>
    </Paper>
  </Box>
)

export const CompartmentsThatNeedsAction: React.FC<Props> = () => {
  const {data: boxes = [], loading, error} = useAllBoxesForActions()
  const {data: allOrganisations = []} = useAllOrganisations({})
  const [filter, setFilter] = usePersistedState<CompartmentThatNeedsActionFilterState>('actionFilter_v3', initialFilterState)

  const [showMap, setShowMap] = useState(true)
  const onClearFilter = () => setFilter(initialFilterState)

  const boxIdToOrgId = useMemo(() => {
    const map = new Map<string, string>()
    for (const org of allOrganisations) {
      for (const b of org.boxes ?? []) {
        if (b?.id) map.set(b.id, org.id)
      }
    }
    return map
  }, [allOrganisations])

  const baseRows = useMemo(() => {
    const baseBoxes = boxes.filter((b) => !b.deprecated)
    const rows = createCompartmentsThatNeedsActionData(baseBoxes)
    for (const row of rows) {
      row.organisationId = boxIdToOrgId.get(row.box.id)
    }
    return rows
  }, [boxes, boxIdToOrgId])

  const filteredRows = useMemo(() => {
    return filterCompartmentsThatNeedsActionData(baseRows, filter)
  }, [baseRows, filter])

  const actionMarkers = useMemo(() => {
    if (!showMap) return []
    return resolveActionMarkers(filteredRows)
  }, [filteredRows, showMap])

  const tableHeadCells = useMemo(() => createCompartmentsThatNeedsActionTableHeadCells({contractTypeLabel}), [])

  if (loading) return <LoadingSpinnerContainer />
  if (error) return <div>{error.message}</div>

  const table = (
    <SortablePaginatedTable
      getComparator={compartmentsThatNeedsActionComparator}
      initialRowsPerPage={INITIAL_ROWS_PER_PAGE}
      initialOrderByKey={'box'}
      initialOrder={'asc'}
      rows={filteredRows}
      tableHeadCells={tableHeadCells}
      collapsible={false}
      contentAlign="left"
      rowLinkTo={(row) =>
        generatePath(Routes.COMPARTMENT, {
          boxId: row.box.id,
          compartmentId: row.compartment.id,
        })
      }
    />
  )

  if (filteredRows.length === 0) {
    return (
      <Box sx={{maxWidth: 1800, width: '100%', mx: 'auto'}}>
        <ActionPageFilter filter={filter} onClearFilter={onClearFilter} setFilter={setFilter} />
        <Box mt={2}>
          <SectionHeader compartmentCount={0} showMap={showMap} onToggleMap={() => setShowMap((prev) => !prev)} />
          <Paper variant="outlined">
            <Box p={4} textAlign="center">
              <Typography variant="h4" gutterBottom>
                No actions shown
              </Typography>
              <Typography variant="body1" color="textSecondary">
                There are currently no compartments requiring action
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{mt: 1}}>
                Try clearing or adjusting the filters to see more results.
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{maxWidth: 1800, width: '100%', mx: 'auto'}}>
      <ActionPageFilter filter={filter} onClearFilter={onClearFilter} setFilter={setFilter} />

      <Box mt={2}>
        <SectionHeader compartmentCount={filteredRows.length} showMap={showMap} onToggleMap={() => setShowMap((prev) => !prev)} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: {xs: 'column', lg: 'row'},
            alignItems: {xs: 'stretch', lg: 'flex-start'},
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <Box sx={{flex: showMap ? {lg: '1 1 50%'} : '1 1 100%', minWidth: 0, width: '100%'}}>{table}</Box>

          {showMap && (
            <Box sx={{flex: {lg: '1 1 50%'}, minWidth: {lg: 480}, width: '100%'}}>
              <MapPanel actionMarkers={actionMarkers} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
